var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { hasVariantIdentity, stateKey } from '../config/fieldDefinitionTypes';
import { deriveComponentAffix, hasFormOnlyComponent, parseSummarizedValue, visibleComponents, } from '../codec/codecRegistry';
import { isMergedMultiValue, splitStoredValue } from './mergedMultiValueSubfield';
import { isMultiValueSubfield } from './multiValueSubfield';
import { punctuationTriggers } from './positionalPunctuation';
import { buildHiddenSubfieldLookup, instanceHasContent } from './formConfigEmptyInstanceFilter';
import { loadedSubfieldOrder } from './subfieldOrder';
import { multiValueDefault } from '../formTypes';
/**
 * Finds a component value by type within a field's components array.
 */
var findComponent = function (field, componentType) {
    var _a, _b;
    return ((_b = (_a = field.components) === null || _a === void 0 ? void 0 : _a.find(function (c) { return c.component_type === componentType; })) === null || _b === void 0 ? void 0 : _b.value) || '';
};
/**
 * Creates an empty FieldInstance from a subfield lookup map.
 */
var createEmptyInstance = function (sfMap, fieldDef) {
    var instance = {};
    sfMap.forEach(function (sfDef, code) {
        var sf = fieldDef === null || fieldDef === void 0 ? void 0 : fieldDef.subfields.find(function (s) { return s.code === code; });
        var subVal = { value: (sf === null || sf === void 0 ? void 0 : sf.defaultValue) || '' };
        if (sfDef.hasComponents && sfDef.components) {
            subVal.components = {};
            sfDef.components.forEach(function (comp) {
                subVal.components[comp.componentType] = '';
            });
        }
        if (sfDef.inputType === 'record-picker' || sfDef.inputType === 'vocabulary') {
            subVal.sourceAuthorityId = '';
        }
        // Multi-value subfields (TASK-064) start with an empty `values` array; the
        // legacy `value` is unused on this path but kept for shape consistency.
        // CZNA-1646 — a default is the first value, as `value` holds it for a single subfield;
        // rows the record carries for the code replace it below, exactly as they do `value`.
        if (isMultiValueSubfield(sfDef)) {
            subVal.values = sf ? multiValueDefault(sf) : [];
        }
        instance[code] = subVal;
    });
    return instance;
};
/**
 * Converts ProposalDataField[] to GenericFormState using the given FormConfig.
 * Handles fieldId routing for tags shared by multiple FieldDefs (e.g. 024-isni
 * vs 024-wikidata, 510-previous vs 510-following, 370-country vs 370-place) —
 * see file-level docstring for the discriminator chain.
 */
export var genericProposalDataToForm = function (config, fields) {
    var state = { fields: {} };
    // CZNA-657 — rows this config cannot represent. See `FormPassthrough`: they
    // are stashed here on load so `genericFormToProposalData` can re-emit them,
    // because `PUT /records/{id}` is a full replace and anything missing from the
    // payload is deleted.
    var passthrough = { unrepresented: [], byInstance: {} };
    var nextPassthroughTicket = 0;
    /** The codes the save-side emptiness filter ignores, per tag — CZNA-725 M1. */
    var hiddenHelperCodes = buildHiddenSubfieldLookup(config);
    var allFieldDefs = [];
    // Tag → list of FieldDef variants sharing this MARC tag.
    // Used to derive discriminators dynamically per API field-instance
    // (defaultValue-on-hidden-subfield, then subfield-code-presence).
    var variantsByTag = new Map();
    for (var _i = 0, _a = config.tabs; _i < _a.length; _i++) {
        var tab = _a[_i];
        var _loop_1 = function (fd) {
            var key = stateKey(fd);
            var sfMap = new Map();
            for (var _f = 0, _g = fd.subfields; _f < _g.length; _f++) {
                var sf = _g[_f];
                sfMap.set(sf.code, sf);
            }
            var info = { fieldDef: fd, key: key, sfMap: sfMap };
            allFieldDefs.push(info);
            // Initialize form state
            if (fd.repeatable) {
                var count = fd.minInstances || 0;
                state.fields[key] = Array.from({ length: count }, function () { return createEmptyInstance(sfMap, fd); });
            }
            else {
                state.fields[key] = [createEmptyInstance(sfMap, fd)];
            }
            if (!variantsByTag.has(fd.tag)) {
                variantsByTag.set(fd.tag, []);
            }
            variantsByTag.get(fd.tag).push(info);
        };
        for (var _b = 0, _c = tab.fields; _b < _c.length; _b++) {
            var fd = _c[_b];
            _loop_1(fd);
        }
    }
    // Build lookup: stateKey → FieldDefInfo
    var fieldDefsByKey = new Map();
    for (var _d = 0, allFieldDefs_1 = allFieldDefs; _d < allFieldDefs_1.length; _d++) {
        var info = allFieldDefs_1[_d];
        fieldDefsByKey.set(info.key, info);
    }
    var resolveTargetKey = function (tag, apiFields) {
        var variants = variantsByTag.get(tag);
        // No variants registered, or only one variant that is not a named variant
        // (no fieldId, or the bare tag as its fieldId — CZNA-902) → plain tag.
        if (!variants || variants.length === 0)
            return { key: tag, guessed: false };
        if (variants.length === 1 && !hasVariantIdentity(variants[0].fieldDef)) {
            return { key: variants[0].key, guessed: false };
        }
        // Rule 1: hidden subfield with defaultValue (covers 024 $2='isni',
        // 510 $w='a'/'b', and any future hidden discriminator). Only variants
        // that declare such a discriminator are considered here.
        var variantsWithHiddenDiscr = variants.filter(function (v) {
            return v.fieldDef.subfields.some(function (sf) { return sf.hidden && sf.defaultValue; });
        });
        var _loop_2 = function (v) {
            var discrSf = v.fieldDef.subfields.find(function (sf) { return sf.hidden && sf.defaultValue; });
            var apiSf = apiFields.find(function (f) { return f.subfield_code === discrSf.code; });
            if (apiSf && apiSf.value === discrSf.defaultValue) {
                return { value: { key: v.key, guessed: false } };
            }
        };
        for (var _i = 0, variantsWithHiddenDiscr_1 = variantsWithHiddenDiscr; _i < variantsWithHiddenDiscr_1.length; _i++) {
            var v = variantsWithHiddenDiscr_1[_i];
            var state_1 = _loop_2(v);
            if (typeof state_1 === "object")
                return state_1.value;
        }
        // Rule 2: subfield-code presence. Used only when no variant of this tag
        // relies on a hidden discriminator (e.g. 370-country vs 370-place are
        // distinguished purely by which subfield code is present). Skipping this
        // when hidden discriminators exist preserves the `${tag}-custom`
        // fallback for cases like 024 with an unknown $2 source.
        var apiCodes = [];
        apiFields.forEach(function (f) {
            var code = f.subfield_code;
            if (code && apiCodes.indexOf(code) === -1) {
                apiCodes.push(code);
            }
        });
        if (apiCodes.length > 0 && variantsWithHiddenDiscr.length === 0) {
            var best = null;
            for (var _a = 0, variants_1 = variants; _a < variants_1.length; _a++) {
                var v = variants_1[_a];
                var variantCodes = v.fieldDef.subfields
                    .filter(function (sf) { return !sf.hidden; })
                    .map(function (sf) { return sf.code; });
                // Every API code must be declared in this variant.
                var allDeclared = true;
                var matches = 0;
                for (var _b = 0, apiCodes_1 = apiCodes; _b < apiCodes_1.length; _b++) {
                    var code = apiCodes_1[_b];
                    if (variantCodes.indexOf(code) === -1) {
                        allDeclared = false;
                        break;
                    }
                    matches += 1;
                }
                if (allDeclared) {
                    // Prefer the variant with the most matched codes; on tie, prefer the
                    // one whose declared subfield set is smaller (tightest fit). Full ties
                    // (same matches, same size) keep the first-encountered variant —
                    // deterministic per FormConfig insertion order.
                    if (!best
                        || matches > best.matches
                        || (matches === best.matches && variantCodes.length < best.size)) {
                        best = { key: v.key, matches: matches, size: variantCodes.length };
                    }
                }
            }
            if (best)
                return { key: best.key, guessed: false };
        }
        // Rule 3: ${tag}-custom fallback (preserves prior behavior for 024 with
        // unknown $2 — routes to the user-typed identifier variant).
        var customKey = "".concat(tag, "-custom");
        if (state.fields[customKey] !== undefined) {
            return { key: customKey, guessed: false };
        }
        // Rule 4: plain tag.
        if (state.fields[tag] !== undefined)
            return { key: tag, guessed: false };
        // Rule 5 (CZNA-723): declared-code coverage, preferring a variant that
        // makes no claim of its own.
        //
        // This is the path a `550 $a $7` takes — a subject relationship the record
        // states nothing about. Rule 1 cannot match (there is no `$w` to match),
        // Rule 2 is skipped because siblings of this tag DO use a hidden
        // discriminator, and 5XX has neither a `-custom` variant nor a plain-tag
        // key. So it used to fall through to the last resort below and land on
        // `550-broader`, whose hidden `$w='g'` then said "širší termín" — on 22 345
        // SUBJECT records, and the same shape on 551/555/510/511 for ~67 800 in
        // total.
        //
        // The neutral variant it should have gone to already existed in the shipped
        // templates (`550-related`, `551-related`, `555-related` — "Asociovaný
        // termín", which is precisely what a 5XX with no `$w` is under NK
        // methodology). Nothing routed to it.
        //
        // Two deliberate details:
        //  - Hidden subfields count as DECLARED here. `$7` is hidden on every 5XX
        //    variant, and the existing Rule 2 filters hidden codes out of the
        //    candidate's code list — which would disqualify every variant for an
        //    instance carrying `$7`, i.e. all of them.
        //  - A variant with no unmatched hidden discriminator outranks one that has
        //    it, ahead of any tightness tie-break. On 551 all three variants declare
        //    three codes, so a size-only tie-break keeps insertion order and hands
        //    the instance to `551-broader` again.
        if (apiCodes.length > 0) {
            var pick = null;
            var covering = variants.filter(function (v) {
                var declared = v.fieldDef.subfields.map(function (sf) { return sf.code; });
                return apiCodes.every(function (code) { return declared.indexOf(code) !== -1; });
            });
            for (var _c = 0, covering_1 = covering; _c < covering_1.length; _c++) {
                var v = covering_1[_c];
                var cand = {
                    key: v.key,
                    // "No UNMATCHED discriminator", not "no discriminator at all". The two
                    // differ for a variant declaring two hidden defaults, where Rule 1
                    // above inspects only the first (`.find`) and so can leave the second
                    // unmatched while still not returning. No shipped config is in that
                    // position, but the looser predicate would have made the comment
                    // above false rather than merely unexercised.
                    neutral: !v.fieldDef.subfields.some(function (sf) { return sf.hidden
                        && sf.defaultValue
                        && !apiFields.some(function (f) { return f.subfield_code === sf.code && f.value === sf.defaultValue; }); }),
                    size: v.fieldDef.subfields.length,
                };
                if (!pick
                    || (cand.neutral && !pick.neutral)
                    || (cand.neutral === pick.neutral && cand.size < pick.size)) {
                    pick = cand;
                }
            }
            if (pick)
                return { key: pick.key, guessed: false };
        }
        // Last-resort: warn and fall back to the first variant so data isn't
        // silently dropped (orchestrator can still inspect it in form state).
        // Reported as `guessed`, which is what makes `unassertedDiscriminators`
        // neutralise that variant's hidden discriminator regardless of how many
        // variants the tag has — so landing here mislabels the field on screen but
        // does not fabricate MARC.
        if (typeof console !== 'undefined' && console.warn) {
            console.warn("[genericProposalDataToForm] No FieldDef variant matched tag '".concat(tag, "' with codes [").concat(apiCodes.join(','), "] \u2014 routing to '").concat(variants[0].key, "'."));
        }
        return { key: variants[0].key, guessed: true };
    };
    /**
     * CZNA-723 — hidden discriminator codes on the chosen variant that this
     * incoming instance does not assert.
     *
     * <p>A hidden subfield with a `defaultValue` on a tag that has SEVERAL
     * variants is a discriminator: it is how `550-broader` differs from
     * `550-narrower`. Stamping it onto an instance that never carried it invents
     * a relationship type. Returning the code here empties the slot and marks it
     * `suppressDefault`, which is what stops the save path putting the default
     * straight back.
     *
     * <p>Rule 1 routings yield nothing by construction — they matched on exactly
     * this value — so no separate "which rule fired" bookkeeping is needed. An
     * instance carrying the code with some OTHER value also counts as unasserted;
     * the marker is set and the API's own value is written over the emptied slot
     * a few lines later, so it is emitted verbatim.
     *
     * <p><b>Where the boundary is, and what it deliberately leaves standing.</b>
     * Suppression applies when the tag has SEVERAL variants, or when the routing
     * was a `guessed` last resort. A single-variant tag reached without guessing
     * keeps its declared defaults: `040$b=cze`, `040$e=rda`, `370$2=czenas`,
     * `450$2=czenas` are that field's constant in the template, in the same
     * variant as everything else the field holds, so stamping them is the
     * template author's decision rather than a consequence of picking a variant.
     *
     * <p>That boundary is not free, and it is the one thing a reader should not
     * take on trust: `uniform-title.json` ships `500` and `510` as single-variant
     * tags each declaring a hidden `$w` with `defaultValue: "r"` — MARC's
     * "relationship designation is in `$i`/`$4`" — so those links DO still acquire
     * a `$w` they did not carry. Measured on the corpus 2026-08-31 (1 271 528
     * active records) that is 38 links over ~35 records, 27 of them with no `$i`
     * for the `$w=r` to point at; against the ~67 800 this ticket fixes. Whether
     * the template should declare it at all is NK's call on their own artifact and
     * the inverse mistake would DELETE an intended assertion, so it is
     * **CZNA-894**, not a widening of this rule. `corpusRoundTrip.test.ts` bounds
     * it rather than excluding it, so it cannot grow unnoticed.
     */
    var unassertedDiscriminators = function (info, apiFields, guessed) {
        var variants = variantsByTag.get(info.fieldDef.tag);
        var discriminated = (!!variants && variants.length >= 2) || guessed;
        if (!discriminated)
            return [];
        return info.fieldDef.subfields
            .filter(function (sf) { return sf.hidden && sf.defaultValue; })
            .filter(function (sf) { return !apiFields.some(function (f) { return f.subfield_code === sf.code && f.value === sf.defaultValue; }); })
            .map(function (sf) { return sf.code; });
    };
    // Group API fields by tag + field_index
    var grouped = new Map();
    for (var _e = 0, fields_1 = fields; _e < fields_1.length; _e++) {
        var field = fields_1[_e];
        var tag = field.field_tag;
        if (!grouped.has(tag)) {
            grouped.set(tag, new Map());
        }
        var tagMap = grouped.get(tag);
        if (!tagMap.has(field.field_index)) {
            tagMap.set(field.field_index, []);
        }
        tagMap.get(field.field_index).push(field);
    }
    // Track which repeatable keys have been populated from API data —
    // on first API entry, clear default empty instances (minInstances padding).
    var populatedKeys = new Set();
    /**
     * `${targetKey}#${instanceIndex}` for every instance this load has already
     * written API data into — how a repeated non-repeatable field is told apart
     * from the first occurrence (CZNA-725 M2).
     */
    var populatedInstances = new Set();
    // Populate state from grouped data
    grouped.forEach(function (indexMap, tag) {
        var sortedIndices = Array.from(indexMap.keys()).sort(function (a, b) { return a - b; });
        sortedIndices.forEach(function (fieldIndex) {
            var _a, _b, _c;
            var apiFields = indexMap.get(fieldIndex);
            var _d = resolveTargetKey(tag, apiFields), targetKey = _d.key, guessed = _d.guessed;
            var info = fieldDefsByKey.get(targetKey);
            if (!info) {
                // CZNA-657: no FieldDef holds this tag, so the form has nowhere to put
                // it and the save path has nothing to emit it from. Preserve verbatim
                // rather than let the round-trip swallow it (the pre-CZNA-657 `return`
                // here is what deleted `GEOGRAPHIC 550`/`665`, every type's
                // `003`/`005`/`008`, and the national 9XX block on save).
                (_a = passthrough.unrepresented).push.apply(_a, apiFields);
                return;
            }
            var fieldDef = info.fieldDef, sfMap = info.sfMap;
            // CZNA-657: a control field is DECLARED but deliberately never written by
            // the form (CZNA-455 — 001/003/005/008 are system-assigned, and the save
            // path returns early on `fieldDef.control_field`). Declared-but-unwritten
            // is the same data loss as undeclared, so it takes the same route. The
            // instance itself is left exactly as the config-walk built it, so the
            // read-only display of 001 is unchanged.
            if (fieldDef.control_field) {
                (_b = passthrough.unrepresented).push.apply(_b, apiFields);
                return;
            }
            // CZNA-725 (M1) — an instance none of whose rows reaches a form control
            // is, for this form, a field it cannot show at all: the same position as
            // a tag with no FieldDef, one level down. `PERSONAL 368` is declared in
            // seven variants and not one of them has `$a`, so `368 $a "oběti
            // komunismu"` routed into an empty instance, the save-side emptiness
            // filter (TASK-103) dropped that instance as blank, and the rows the
            // instance was carrying (CZNA-657) went with it. Carried rows are
            // deliberately NOT content there — a field whose visible value the
            // cataloguer blanked is a deleted field — so the fix belongs here, where
            // "blank because deleted" and "blank because never representable" are
            // still distinguishable.
            //
            // "Content" is judged by the save filter's own predicate
            // (`instanceHasContent`: a non-blank value or component, helper codes —
            // `hidden` with a `defaultValue` — ignored), over the rows under a code
            // this variant declares: the only rows that land in a control.
            var reachesAControl = instanceHasContent(tag, apiFields
                .filter(function (f) { return sfMap.has(f.subfield_code || ''); })
                .map(function (f) { return ({ subfieldCode: f.subfield_code || '', value: f.value || '', components: f.components }); }), hiddenHelperCodes);
            if (!reachesAControl) {
                if (typeof console !== 'undefined' && console.warn) {
                    console.warn("[genericProposalDataToForm] Tag ".concat(tag, " (index ").concat(fieldIndex, ") carries no subfield '").concat(targetKey, "' can show ")
                        + "([".concat(apiFields.map(function (f) { return f.subfield_code || ''; }).join(','), "]); preserving the field verbatim."));
                }
                (_c = passthrough.unrepresented).push.apply(_c, apiFields);
                return;
            }
            var isRepeatable = !!fieldDef.repeatable;
            // On first API data for a repeatable key, clear default empty instances
            if (isRepeatable && !populatedKeys.has(targetKey)) {
                state.fields[targetKey] = [];
                populatedKeys.add(targetKey);
            }
            var instanceIndex;
            if (isRepeatable) {
                instanceIndex = state.fields[targetKey].length;
                state.fields[targetKey].push(createEmptyInstance(sfMap, fieldDef));
            }
            else if (populatedInstances.has("".concat(targetKey, "#0"))) {
                // CZNA-725 (M2) — MARC repeated a field this variant declares
                // non-repeatable (`AUTHOR_WORK 382` twice). It used to be written over
                // instance 0: the second occurrence's declared values replaced the
                // first's, and its passthrough ticket replaced the first's, orphaning
                // those rows — both instances lost data on a plain open-and-save.
                //
                // It gets an instance of its own instead. The renderer draws only
                // `fields.<key>.0` for a non-repeatable field, so this one is not on
                // screen and cannot be edited — the same standing as a passthrough row
                // — but it lives in form state, so the save path emits it as its own
                // `field_index` and every consumer of instance 0 (heading preview,
                // derived fields) keeps reading the FIRST occurrence, not the last.
                if (typeof console !== 'undefined' && console.warn) {
                    console.warn("[genericProposalDataToForm] Tag ".concat(tag, " (index ").concat(fieldIndex, ") repeats non-repeatable '").concat(targetKey, "'; ")
                        + 'kept as a separate, unrendered instance and re-emitted on save.');
                }
                instanceIndex = state.fields[targetKey].length;
                state.fields[targetKey].push(createEmptyInstance(sfMap, fieldDef));
            }
            else {
                instanceIndex = 0;
                if (!state.fields[targetKey][0]) {
                    state.fields[targetKey][0] = createEmptyInstance(sfMap, fieldDef);
                }
            }
            var instance = state.fields[targetKey][instanceIndex];
            populatedInstances.add("".concat(targetKey, "#").concat(instanceIndex));
            // CZNA-723 — before anything is written from the API, clear the hidden
            // discriminator defaults this record never asserted. Order matters: the
            // API loop below runs after, so an instance that DOES carry the code
            // (with any value) still gets that value written over the emptied slot.
            //
            // Every MARC instance now has a form instance of its own (see M2 above),
            // so one occurrence's silence can no longer erase a `$w` another
            // occurrence asserted — which is what an `alreadyPopulated` exemption
            // here used to guard against.
            var toSuppress = unassertedDiscriminators(info, apiFields, guessed);
            toSuppress.forEach(function (code) {
                var _a;
                var subVal = instance[code];
                if (!subVal)
                    return;
                subVal.value = '';
                subVal.suppressDefault = true;
                // Worth a line even though nothing is lost: the instance is now sitting
                // in a variant whose label claims a relationship type the record does
                // not state, which is a mislabelled heading on screen. It is also the
                // signal that told us the fabrication was happening at all.
                if (typeof console !== 'undefined' && console.warn) {
                    console.warn("[genericProposalDataToForm] Tag ".concat(tag, " (index ").concat(fieldIndex, ") routed to '").concat(targetKey, "', whose hidden $").concat(code, " default ")
                        + "'".concat((_a = info.fieldDef.subfields.find(function (sf) { return sf.code === code; })) === null || _a === void 0 ? void 0 : _a.defaultValue, "' this record does not assert \u2014 not stamping it."));
                }
            });
            // Read indicators from the first API field
            var firstField = apiFields[0];
            if (firstField) {
                instance._ind1 = { value: firstField.indicator1 || ' ' };
                instance._ind2 = { value: firstField.indicator2 || ' ' };
            }
            // Group apiFields by subfield_code so that multi-value subfields can
            // aggregate all rows for the same code into `values: []`. Single-value
            // subfields fall back to the first row (with a defensive warning if
            // multiple rows arrive unexpectedly).
            var apiBySubcode = new Map();
            apiFields.forEach(function (f) {
                var code = f.subfield_code || '';
                if (!apiBySubcode.has(code))
                    apiBySubcode.set(code, []);
                apiBySubcode.get(code).push(f);
            });
            // CZNA-657 — rows under a subfield code this FieldDef does not declare.
            // The field IS on screen, so these belong to THIS instance and must
            // follow it: keyed by a ticket stashed in the instance, never by
            // field_index (a reorder would re-attach a `$7` to the wrong heading).
            var unrepresentedSubfields = [];
            apiBySubcode.forEach(function (rows, code) {
                var _a, _b, _c, _d, _e;
                var sfDef = sfMap.get(code);
                if (!sfDef || !instance[code]) {
                    unrepresentedSubfields.push.apply(unrepresentedSubfields, rows);
                    return;
                }
                if (isMultiValueSubfield(sfDef)) {
                    // TASK-064 multi-value path — aggregate rows into `values: []`,
                    // ordered by subfield_index (falls back to encounter order for ties).
                    var sorted = __spreadArray([], rows, true).sort(function (a, b) {
                        var _a, _b;
                        var ai = (_a = a.subfield_index) !== null && _a !== void 0 ? _a : 0;
                        var bi = (_b = b.subfield_index) !== null && _b !== void 0 ? _b : 0;
                        return ai - bi;
                    });
                    var nonEmpty = sorted.filter(function (r) { var _a; return ((_a = r.value) !== null && _a !== void 0 ? _a : '').length > 0; });
                    // CZNA-449 — a MARC-non-repeatable multi-select subfield is stored as
                    // ONE row (terms joined) plus one component per term. Three shapes can
                    // arrive and all three must load into the picker:
                    if (isMergedMultiValue(sfDef) && nonEmpty.length === 1) {
                        var row_1 = nonEmpty[0];
                        var declaredType_1 = sfDef.multiValueComponentType;
                        var terms = ((_a = row_1.components) !== null && _a !== void 0 ? _a : [])
                            .filter(function (c) { return c.component_type === declaredType_1; })
                            .map(function (c) { var _a; return ((_a = c.value) !== null && _a !== void 0 ? _a : '').trim(); })
                            .filter(function (t) { return t.length > 0; });
                        if (terms.length > 0) {
                            // (a) written by this form — the components are authoritative, so
                            // no parsing of the joined text is involved.
                            instance[code].values = terms.map(function (t) { return ({ value: t }); });
                        }
                        else {
                            // (b) imported or saved before CZNA-449 — no components, so recover
                            // the terms from the joined text. `splitStoredValue` only splits
                            // when re-joining reproduces the value byte-for-byte, so a no-op
                            // save cannot silently reformat it.
                            var recovered_1 = splitStoredValue(row_1.value);
                            instance[code].values = recovered_1.map(function (value, i) {
                                var entry = { value: value };
                                // The FK is subfield-level: it can only belong to a lone term.
                                if (i === 0 && recovered_1.length === 1 && row_1.source_authority_id) {
                                    entry.sourceAuthorityId = row_1.source_authority_id;
                                }
                                return entry;
                            });
                        }
                        return;
                    }
                    // (c) several rows for a non-repeatable subfield — legacy defect data
                    // (in the corpus: a citation mis-coded into 670$b, or a row duplicated
                    // from a sibling 670). Loaded as separate entries ON PURPOSE: the
                    // cataloger then SEES the wrong term sitting in the picker and can
                    // remove it, instead of it being folded into the joined value where it
                    // would be invisible. Also the normal path for genuinely repeatable
                    // multi-value subfields (100$c / 400$c).
                    instance[code].values = nonEmpty.map(function (r) {
                        var entry = { value: r.value };
                        if (r.source_authority_id)
                            entry.sourceAuthorityId = r.source_authority_id;
                        // CZNA-1646 — as the single-value path below keeps `storedComponents` (CZNA-1412).
                        if (r.components && r.components.length > 0) {
                            entry.storedComponents = {
                                value: r.value,
                                components: r.components.map(function (c) { return ({ component_type: c.component_type, value: c.value }); }),
                            };
                        }
                        return entry;
                    });
                    // CZNA-1412 — what the save compares against before it adds the ISBD
                    // period in front of this subfield (`positionalPunctuation`).
                    if (punctuationTriggers(fieldDef.tag).has(code)) {
                        instance[code].loadedValues = nonEmpty.map(function (r) { return r.value.trim(); }).filter(function (v) { return v.length > 0; });
                    }
                    return;
                }
                // Single-value path. Only the first row can reach a form control, but
                // the rest are real MARC data — CZNA-722. They used to be dropped here,
                // and because `PUT /records/{id}` is a full replace, saving deleted
                // them: measured on the corpus (2026-08-25, 1.27 M active records)
                // 484 145 records lost data by being opened and saved, `PERSONAL 372$a`
                // (315 993 records) and `374$a` (281 509) worst. A person with five
                // professions kept one.
                //
                // Why they are carried rather than rendered: the EFC marks these
                // subfields `repeatable: true` (the backend compiler reads it off the
                // MARC catalog) while `isMultiValueSubfield` decides multi-ness from
                // `inputType`/`multiValue` alone, so the two disagree on 161 subfields
                // across the ten types. Making all 161 render as chip lists is a
                // customer-visible change to how the form looks and needs NK's view on
                // which of them should be lists at all; preserving the data does not.
                // So this stops the deletion and leaves editability to that decision.
                //
                // Note this is NOT the CZNA-657 case. That one covers rows under a
                // subfield code the config never declares; here the code IS declared,
                // just as single-value, so the declared branch claims the rows and the
                // undeclared-bucket above never sees them. Same fate, different hole.
                if (rows.length > 1) {
                    unrepresentedSubfields.push.apply(unrepresentedSubfields, rows.slice(1));
                    if (typeof console !== 'undefined' && console.warn) {
                        console.warn("[genericProposalDataToForm] Tag ".concat(tag, " (index ").concat(fieldIndex, ") has ").concat(rows.length, " rows for code '").concat(code, "' but subfield is single-value; editing the first, preserving ").concat(rows.length - 1, " on save."));
                    }
                }
                var apiField = rows[0];
                if (sfDef.hasComponents && sfDef.components) {
                    var ind1 = (firstField === null || firstField === void 0 ? void 0 : firstField.indicator1) || ' ';
                    var shown = visibleComponents(sfDef, ind1);
                    // CZNA-1007 — the persisted components cannot represent this split,
                    // so the flat value is the authority and has to be decoded. True
                    // exactly when a component visible under this indicator is
                    // `formOnly`: `100$a` with `ind1='3'` stores FAMILY_NAME = the WHOLE
                    // `$a` ("Rottovi (rodina)") because the ontology binds it
                    // `part: wholeValue`, and the qualifier is not a row at all. Seeding
                    // from those rows would put the qualifier inside the name box and
                    // leave the qualifier box empty.
                    //
                    // This used to be `code === 'a' && FAMILY_HEADING_TAGS.has(tag) &&
                    // ind1 === '3'` — one of four copies of that test, in four files.
                    var decodeFromFlatValue = hasFormOnlyComponent(sfDef, ind1);
                    // Seed each component from the API row's structured `components[]`.
                    for (var _i = 0, _f = sfDef.components; _i < _f.length; _i++) {
                        var comp = _f[_i];
                        if (instance[code].components) {
                            instance[code].components[comp.componentType] = findComponent(apiField, comp.componentType);
                        }
                    }
                    // CZNA-310: imported (Aleph) records store component subfields as a
                    // flat `$a`/`$d` string with NO `components[]` metadata, so the loop
                    // above leaves every slot empty — the form inputs render blank and a
                    // no-op save would drop the heading (the encoder skips a component
                    // subfield whose component map is entirely empty). When the row has
                    // a flat value but no usable components, parse the value back into
                    // its components (inverse of the save-side summarizeFn) so the form
                    // inputs render filled.
                    //
                    // The summarize/parse pair is LOSSY for several real Aleph shapes
                    // (bare year `"1850"` ↔ `"1850-"`, death-only `"-1951"` ↔ `""`,
                    // fuzzy `"approx. 1900"`, surname-comma `"Novák,"`). To guarantee a
                    // byte-identical no-op round-trip we ALSO stash the original flat
                    // value in `importedValue`; the save path re-emits it verbatim while
                    // the components remain unedited. See `genericFormToProposalData`.
                    var componentsArrived = sfDef.components.some(function (comp) { var _a; return (((_a = instance[code].components) === null || _a === void 0 ? void 0 : _a[comp.componentType]) || '').length > 0; });
                    var hasParsedComponents = componentsArrived && !decodeFromFlatValue;
                    var hasFlatValue = (apiField.value || '').trim().length > 0;
                    if (!hasParsedComponents && hasFlatValue && sfDef.summarizeFn) {
                        var parsed = parseSummarizedValue(sfDef, apiField.value, ind1);
                        if (instance[code].components) {
                            for (var _g = 0, shown_1 = shown; _g < shown_1.length; _g++) {
                                var comp = shown_1[_g];
                                // Written even when the decoder returned '' for it — the
                                // qualifier box must come up empty on the 23 corpus headings
                                // that carry none, not holding the row's leftover text.
                                if (comp.componentType in parsed) {
                                    instance[code].components[comp.componentType] = parsed[comp.componentType];
                                }
                            }
                            // CZNA-1007, generalising the family branch's defensive
                            // `GIVEN_NAME = ''`: a component the current indicator does not
                            // show is not part of this value, and leaving the row's copy in
                            // form state puts it back into `$a` on the next save.
                            //
                            // By TYPE, not by ComponentDef: one componentType may be
                            // declared twice with different `visibleWhen` — `100$a` carries
                            // FAMILY_NAME once labelled "Příjmení" (`ind1='1'`) and once
                            // "Jméno rodiny" (`ind1='3'`) — so testing ComponentDef identity
                            // clears the entry that the visible twin had just filled.
                            var shownTypes = shown.map(function (c) { return c.componentType; });
                            for (var _h = 0, _j = sfDef.components; _h < _j.length; _h++) {
                                var comp = _j[_h];
                                if (shownTypes.indexOf(comp.componentType) === -1) {
                                    instance[code].components[comp.componentType] = '';
                                }
                            }
                        }
                        // Authoritative original flat value — re-emitted verbatim on save
                        // until the user edits the derived components. Only for the
                        // CZNA-310 case: under `decodeFromFlatValue` the row DID arrive
                        // with components, and `persisted` below is the shield that
                        // applies. Setting both would make the passthrough outrank the
                        // snapshot the ind1-switch clearing knows how to reset.
                        if (!componentsArrived) {
                            instance[code].importedValue = apiField.value;
                        }
                    }
                    // CZNA-898 — the other half of the same guarantee, for the rows
                    // that DID arrive with components. Persisted components are exactly
                    // what disarms the passthrough above (`hasParsedComponents`), so
                    // before this snapshot those rows had no authoritative original at
                    // all and the save re-emitted whatever the codec composed: on
                    // 637 353 of 952 440 `100$a` rows and 79 376 of 130 908 `110$a`
                    // rows that is not the value the corpus holds.
                    //
                    // Snapshotted rather than recomputed on save, because "did the
                    // cataloguer touch this" is a question the load state answers
                    // exactly and every recomputation answers by heuristic.
                    //
                    // CZNA-1007 — also taken under `decodeFromFlatValue`, and for a
                    // WORSE reason than the generic path: that save branch emits `$a`
                    // with no `components` at all, so a plain open-and-save deleted the
                    // stored component rows of all 1 349 `100$a`/`400$a` records with
                    // `ind1='3'`. The value survives most of the time and that is what
                    // made it look benign — but not always: `parseFamilyA` normalises
                    // whitespace, so `Habsburkové  (rod)` came back `Habsburkové (rod)`.
                    if (hasParsedComponents || (decodeFromFlatValue && hasFlatValue)) {
                        // Keyed on what the SAVE reads, which under `decodeFromFlatValue`
                        // is the VISIBLE set — the hidden components were just cleared and
                        // including them would make every such row look edited.
                        var declared = {};
                        for (var _k = 0, _l = (decodeFromFlatValue ? shown : sfDef.components); _k < _l.length; _k++) {
                            var comp = _l[_k];
                            declared[comp.componentType] = ((_b = instance[code].components) === null || _b === void 0 ? void 0 : _b[comp.componentType]) || '';
                        }
                        var affix = deriveComponentAffix(sfDef, declared, apiField.value || '', ind1);
                        instance[code].persisted = {
                            value: apiField.value || '',
                            // Copied, not aliased: the form state is mutable and this is
                            // the record of what arrived.
                            components: ((_c = apiField.components) !== null && _c !== void 0 ? _c : []).map(function (c) { return ({ component_type: c.component_type, value: c.value }); }),
                            declared: declared,
                            prefix: (_d = affix === null || affix === void 0 ? void 0 : affix.prefix) !== null && _d !== void 0 ? _d : '',
                            suffix: (_e = affix === null || affix === void 0 ? void 0 : affix.suffix) !== null && _e !== void 0 ? _e : '',
                        };
                    }
                    instance[code].value = apiField.value;
                }
                else {
                    instance[code].value = apiField.value;
                    // CZNA-1412 — components the config does not declare here (`110$c`
                    // PLACE_QUALIFIER); see `SubfieldValue.storedComponents`.
                    if (apiField.components && apiField.components.length > 0) {
                        instance[code].storedComponents = {
                            value: apiField.value || '',
                            components: apiField.components.map(function (c) { return ({ component_type: c.component_type, value: c.value }); }),
                        };
                    }
                }
                // Restore sourceAuthorityId
                if (apiField.source_authority_id) {
                    instance[code].sourceAuthorityId = apiField.source_authority_id;
                }
            });
            // CZNA-1382 — the order the subfields came in, when the save would not
            // reproduce it by itself (see `subfieldOrder.ts`).
            var order = loadedSubfieldOrder(fieldDef, apiFields, new Set(unrepresentedSubfields));
            if (order) {
                instance._order = { value: order.join('') };
            }
            // CZNA-657 — hand this instance its passthrough ticket. Only when there
            // is something to carry, so an instance with nothing undeclared stays
            // byte-identical to what it was before this ticket.
            if (unrepresentedSubfields.length > 0) {
                // One ticket per instance is enough: every MARC field instance gets a
                // form instance of its own (CZNA-725 M2), so no second occurrence can
                // arrive here and overwrite this one's ticket.
                var ticket = "pt".concat(nextPassthroughTicket);
                nextPassthroughTicket += 1;
                passthrough.byInstance[ticket] = unrepresentedSubfields;
                instance._ptId = { value: ticket };
            }
        });
    });
    // Attach only when non-empty: a form with nothing to preserve keeps the exact
    // state shape it had before CZNA-657, so no consumer sees a new key.
    if (passthrough.unrepresented.length > 0
        || Object.keys(passthrough.byInstance).length > 0) {
        state.passthrough = passthrough;
    }
    return state;
};
